const Moment = require('moment');
const { capitalize } = require('./strings');
const {
  COMMODITIES,
  CURRENCIES,
  DAYS,
  DEVICE_CHARGE_STATUS,
  DEVICE_KINDS,
  DEVICE_MFGS,
  DEVICE_SUB_BRAND,
  EVENT_CATEGORY,
  EVENT_INITIATOR,
  EVENT_TYPE,
  LINKED_ACCOUNT_TYPES,
  MONTHS,
  PAYMENT_TYPES,
  STATE_NAMES,
  STATE_NAMES_AU,
  STATE_NAMES_CA,
  TIME_ZONES,
  VEHICLE_MAKES,
} = require('../constants');

function commodityByKey(key) {
  const co = COMMODITIES.find((commodity) => commodity.key === key);
  return co ? co.name : null;
}

function commodityByName(name) {
  const co = COMMODITIES.find((commodity) => commodity.name === name);
  return co ? co.key : null;
}

function currencyByKey(key) {
  const cu = CURRENCIES.find((currency) => currency.key === key);
  return cu ? cu.name : null;
}

function currencyByName(name) {
  const cu = CURRENCIES.find((currency) => currency.name === name);
  return cu ? cu.key : null;
}

function deviceChargeStatus(status) {
  if (typeof status === 'string') {
    if (status in DEVICE_CHARGE_STATUS) {
      return DEVICE_CHARGE_STATUS[status];
    }
    return status;
  }
  return DEVICE_CHARGE_STATUS.default;
}

function deviceKind(kind) {
  if (typeof kind === 'string') {
    if (kind in DEVICE_KINDS) {
      return DEVICE_KINDS[kind];
    }
    return kind;
  }
  return DEVICE_KINDS.default;
}

function deviceMfg(mfg) {
  if (typeof mfg === 'string') {
    if (mfg in DEVICE_MFGS) {
      return DEVICE_MFGS[mfg];
    }
    return capitalize(mfg);
  }
  return DEVICE_MFGS.default;
}

function deviceSubBrand(brand) {
  if (typeof brand === 'string') {
    if (brand in DEVICE_SUB_BRAND) {
      return DEVICE_SUB_BRAND[brand];
    }
    return capitalize(brand);
  }
  return DEVICE_SUB_BRAND.default;
}

function eventCategory(category) {
  if (typeof category === 'string') {
    if (category in EVENT_CATEGORY) {
      return EVENT_CATEGORY[category];
    }
    return capitalize(category);
  }
  return EVENT_CATEGORY.default;
}

function eventInitiator(initiator) {
  if (initiator in EVENT_INITIATOR) {
    return EVENT_INITIATOR[initiator];
  }
  return EVENT_INITIATOR.default;
}

function eventType(type) {
  if (type in EVENT_TYPE) {
    return EVENT_TYPE[type];
  }
  return EVENT_TYPE.default;
}

function linkedAccountType(type) {
  if (typeof type === 'string') {
    if (type in LINKED_ACCOUNT_TYPES) {
      return LINKED_ACCOUNT_TYPES[type];
    }
    return type;
  }
  return LINKED_ACCOUNT_TYPES.default;
}

function paymentType(type) {
  if (typeof type === 'string') {
    if (type in PAYMENT_TYPES) {
      return PAYMENT_TYPES[type];
    }
    return capitalize(type);
  }
  return PAYMENT_TYPES.default;
}

function stateByAbbreviation(abbreviation, countryCode) {
  if (typeof abbreviation === 'string') {
    let st = '';
    switch (countryCode) {
      case 'AU':
        st = STATE_NAMES_AU.find((state) => state.abbreviation === abbreviation);
        return st ? st.name : capitalize(abbreviation);
      case 'CA':
        st = STATE_NAMES_CA.find((state) => state.abbreviation === abbreviation);
        return st ? st.name : capitalize(abbreviation);
      default:
        st = STATE_NAMES.find((state) => state.abbreviation === abbreviation);
        return st ? st.name : capitalize(abbreviation);
    }
  }
  return 'Unknown';
}

function stateByName(name, countryCode) {
  if (typeof name === 'string') {
    const bigName = name.toUpperCase();
    let st = '';
    switch (countryCode) {
      case 'AU':
        st = STATE_NAMES_AU.find((state) => state.name === bigName);
        return st ? st.abbreviation : capitalize(name);
      case 'CA':
        st = STATE_NAMES_CA.find((state) => state.name === bigName);
        return st ? st.abbreviation : capitalize(name);
      default:
        st = STATE_NAMES.find((state) => state.name === bigName);
        return st ? st.abbreviation : capitalize(name);
    }
  }
  return 'Unknown';
}

function time(input) {
  const hours = Math.floor(input);
  const minutes = (input - hours) * 60;

  return `${hours.toString()}:${(`00${Math.round(minutes).toString()}`).slice(-2)}`;
}

function timeAmPm(input) {
  if (input == null) {
    return 'ALL DAY';
  }
  let hours = Math.floor(input);
  let minutes = (input - hours) * 60;
  let ampm = 'AM';

  if (hours > 12) {
    ampm = 'PM';
    hours -= 12;
  } else if (hours === 12) {
    ampm = 'PM';
  } else if (hours === 0) {
    ampm = 'AM';
    hours = 12;
  }
  minutes = Math.round(minutes);

  return hours.toString() + (minutes !== 0 ? `:${(`00${Math.round(minutes).toString()}`).slice(-2)}` : '') + ampm;
}

function timezoneByName(name) {
  if (typeof name === 'string') {
    const tz = TIME_ZONES.find((timezone) => timezone.name === name);
    return tz ? tz.value : null;
  }
  return null;
}

function timezoneByValue(value) {
  if (typeof value === 'string') {
    const tz = TIME_ZONES.find((timezone) => timezone.value === value);
    return tz ? tz.name : null;
  }
  return null;
}

function vehicleMake(brand) {
  if (typeof brand === 'string') {
    if (brand in VEHICLE_MAKES) {
      return VEHICLE_MAKES[brand];
    }
    return brand;
  }
  return VEHICLE_MAKES.default;
}

/* Auxiliary Functions (NOT EXPORTED) */
function formatDate(date, stringFormat) {
  return Moment(date).format(stringFormat);
}

function localisedDate(date, stringFormat, locale) {
  return Moment(date).locale(locale).format(stringFormat);
}

module.exports = {
  commodityByKey,
  commodityByName,
  currencyByKey,
  currencyByName,
  date: (date, stringFormat = 'L') => formatDate(date, stringFormat),
  dateTime: (date) => formatDate(date, 'LT [on] L'),
  day: (index) => DAYS[index] || 'Unknown',
  deviceChargeStatus,
  deviceKind,
  deviceMfg,
  deviceSubBrand,
  eventCategory,
  eventInitiator,
  eventType,
  linkedAccountType,
  localizedDate: (date, stringFormat = 'L', locale = 'en') => localisedDate(date, stringFormat, locale),
  localizedDateTime: (date, locale = 'en') => localisedDate(date, 'LT [on] L', locale),
  localizedTimeDate: (date, locale = 'en') => localisedDate(date, 'LT [at] L', locale),
  month: (index) => MONTHS[index] || 'Unknown',
  paymentType,
  stateByAbbreviation: (abbreviation, countryCode = 'US') => stateByAbbreviation(abbreviation, countryCode),
  stateByName: (name, countryCode = 'US') => stateByName(name, countryCode),
  time,
  timeAmPm,
  timeDate: (date) => formatDate(date, 'LT [at] L'),
  timezoneByName,
  timezoneByValue,
  vehicleMake,
};
